import React, { useCallback, useEffect, useMemo } from 'react';
import Sidebar from './SideBar';
import Header from './Header';
import AutoWriteEditor from './AutoWriteEditor';
import Qalert from '../shared/Qalert';
import { IconButton } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import { APP_STORE } from '../../utility/store/redux';
import { useDispatch, useSelector } from 'react-redux';
import publicApi from '../../api/publicApi';
import { useRouter } from 'next/router';
import { actionTypes } from '../../constants/actionTypes';
import {
  useSession,
  useQalert,
  usePreFetchData,
  useIntercomUserInfo,
  useIsContentAIUser,
  useSchema,
  useIsFreeTrialContentAIUser,
} from '../../utility/services/customHooks';
import ThreeBarLoader from '../shared/ThreeBarLoader';
import { useIsSubscriberUser, useIsInternalUser } from '../../utility/services/customHooks';
import { isProductionEnv, USER_CLASSIFICATION } from '../../utility/common';
import QsectionHeader from './QsectionHeader';
import { useView } from '../../utility/services/uiHelpers';
import { AuthenticationStatus } from '../../auth/types';
import { signOut } from '../../utility/services/authActions';
import ManageSubscriptions from '../shared/quattr-actions/components/ManageSubscriptions';
import UnderMaintenance from '../content-recommendations/common/underMaintenance';
import { isServerSide } from '../../utility/rendering';
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import CustomAlert from '../shared/CustomAlert';
import { SUBSCRIPTION_STATUS } from '../../constants/common';
import DesktopAlert from './DesktopAlert';

const timeout = 1000 * 60 * process.env.NEXT_PUBLIC_SESSION_EXPIRY;
if (!timeout) {
  throw new Error(`No timeout set. This will cause errors!`);
}

/**
 * @type {import("next").NextComponentType}
 */
const PrivateLayout = (props) => {
  const isMobile = useView('mobileOnly');
  const [open, setOpen] = React.useState(true);
  const router = useRouter();
  const { asPath } = router;
  const pathname = asPath;
  const schemaName = useSchema();
  const customerInfo = useSelector((state) => state.root.customerInfo || []);

  const session = useSession();
  const [options, setOptions] = React.useState([
    { text: 'AI SEO Suite', value: 'AI_SEO_SUITE', disabled: true, hidden: true },
    { text: 'Content AI', value: 'CONTENT_AI', disabled: true, hidden: true },
    { text: 'AI Tools', value: 'QWA_TOOLS', disabled: true, hidden: true },
  ]);

  const getUserType = () => {
    const sessionData = session.session;
    if (!sessionData || isServerSide()) {
      return undefined;
    }

    const userInfo = sessionData?.user;

    if (!userInfo) {
      return undefined;
    }
    if (userInfo?.userType == undefined) {
      const customerData = customerInfo.find((o) => o.cust_code == schemaName);
      const isContentAi = customerData?.sidebar_link_config?.['is-content-enabled-only'];
      if (isContentAi) {
        return USER_CLASSIFICATION.CONTENT_AI;
      }
    }

    return userInfo?.userType;
  };

  const [selectedUserType, setSelectedUserType] = React.useState(null);
  const dispatch = useDispatch();
  const isContentAIUser = useIsContentAIUser();
  // const paths = ['/app/trends/top-trends', '/app/trends/competitive-trends/[id]', '/app/trends/growth-trends/[id]', '/app/opportunities/top-opportunities', '/app/usability-recommendations'];
  const paths = [
    '/app/content-recommendations/editor/',
    '/app/content-recommendations/details/',
    '/app/content-recommendations/content-brief/',
    '/app/content-recommendations/content-brief/details/',
    '/app/content-recommendations/add-page-optimize',
    '/app/manage-queries/intent-settings',
    '/app/manage-categories',
    '/app/manage-queries/query-classification-rules',
    '/app/manage-lists',
    '/app/discoverability/internal-linking-recommendations/view-sample/',
    '/app/discoverability/autonomous-internal-linking/',
    '/app/content-ai-home',
    '/app/ai-tools-home',
    '/app/ai-seo-suite-home',
    '/app/manage-account',
    '/app/add-credits',
    '/app/settings/setup/add-competitors',
    '/app/settings/setup/add-domains',
    '/app/settings/setup/add-branded-keywords',
  ];
  const hideQheaderforPaths = [
    '/app/trends/growth-trends',
    '/app/trends/competitive-trends',
    '/app/content-recommendations/details/',
    '/app/ranking-factors/ranking-factors-summary',
    '/app/manage-queries/intent-settings',
    '/app/manage-categories',
    '/app/manage-queries/query-classification-rules',
    '/app/manage-lists',
    '/app/settings/setup/add-competitors',
    '/app/settings/setup/add-domains',
    '/app/settings/setup/add-branded-keywords',
    // '/app/discoverability/internal-linking-recommendations',
  ];
  const isFullScreenRoute = (paths, pathName) => paths.some((path) => pathName.includes(path));
  const shoudHideQheader = hideQheaderforPaths.some((path) => router.pathname.includes(path));

  const showSubscriberBanner = useSelector((state) => state.root.showSubscriberBanner);
  const subscriberMeta = useSelector((state) => state.root.subscriberMeta);

  const usabilityList = useSelector((state) => state.root.usabilityRecommendationList);
  const showFullScreen =
    useSelector((state) => state.root.showFullScreen) ||
    isFullScreenRoute(paths, router.pathname) ||
    router.pathname.startsWith('/app/ai-tools/');
  const sidebarOpen = useSelector((state) => state.root.sidebarOpen);
  const customPopupConfig = useSelector((state) => state.root.customPopupConfig);
  const [snackConfig, toogleSnackBar] = useQalert();
  const isSubscriberUser = useIsSubscriberUser();
  const isInternalUser = useIsInternalUser();
  // const { authState, authService } = useOktaAuth();

  const loadersArray = useSelector((state) => state.root.loadersArray || []);
  const [
    fetchTopTrends,
    fetchUsabilityRecommendation,
    fetchCustomerInfo,
    getUserPermissions,
    getLooksData,
    fetchUsers,
  ] = usePreFetchData();

  const isSiteUsabilityPage = router.pathname.includes('/app/usability-recommendations');

  const exportHeaders = [
    { label: 'Site', key: `site` },
    { label: 'Rank', key: `rank` },
    { label: 'Usability Improvement', key: `usability_improvement` },
    { label: 'Category', key: `lh_category` },
    { label: 'Resource URL', key: `resourceurl` },
    { label: 'Top Pages', key: `top_pages` },
    { label: 'Top Cat Subcat', key: `top_cat_subcat` },
    { label: 'Monthly Clicks', key: `monthly_clicks` },
    { label: 'Final URL List', key: `finalurl_list` },
    { label: 'Usability Impact', key: `usability_impact` },
    { label: 'URLs Impacted', key: `urls_impacted` },
    { label: 'UUID', key: `uuid` },
    { label: 'Resolution', key: `resolution` },
  ];
  // commented beacuse no need this as we are using logrocket
  // useEffect(() => {
  //   if (session?.session?.user?.email) {
  //     amplitude?.init(process.env.NEXT_PUBLIC_AMPLITUDE_KEY, session?.session?.user?.email, {
  //       defaultTracking: {
  //         sessions: true,
  //       },
  //     });

  //     const sessionReplayTracking = sessionReplayPlugin({
  //       sampleRate: 1, // change this afterwords according to the quota given by amplitude.
  //     });

  //     // Enable session replay
  //     amplitude?.add(sessionReplayTracking);
  //   }
  // }, [session]);

  const isFreeTrailContentAIUser = useIsFreeTrialContentAIUser();

  React.useEffect(() => {
    setOpen(sidebarOpen);
  }, [sidebarOpen]);

  React.useEffect(() => {
    // Listen for logout events
    window.addEventListener('storage', function (event) {
      if (event.key === 'logged_out') {
        // Perform logout
        signOut();
      }
      if (event.key === 'switched_account') {
        window.location.reload();
      }
    });
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      const handleResize = () => {
        const isSmallScreen = document?.documentElement?.clientWidth <= 1200;
        if (isSmallScreen && open) {
          setOpen(false);
        }
      };
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [open]);

  React.useEffect(() => {
    // for easy debugging on non prod environments
    if (!isProductionEnv) {
      window.APP_STORE = APP_STORE;
    }
    const isMobile = document.documentElement.clientWidth < 768;
    if (isMobile) {
      setOpen(false);
    }
    if (session.session && session.authState !== AuthenticationStatus.Loading) {
      if (!isSubscriberUser) {
        // const profile = getUserDetails();
        const profile = session.session;

        if (profile && !isInternalUser) {
          handleSurvey(profile);
        }

        // fetchTopTrends(); // sf cost reduction measure
        // fetchUsabilityRecommendation();
        // this call is a dependency, hence the await
        getUserPermissions();
        getLooksData();
        fetchUsers();
      }
      // this call has a dependency in getUserPermissions();
      fetchCustomerInfo(session.session);
    }
  }, [session?.session]);

  const handleSurvey = async (profile) => {
    try {
      const result = await publicApi.post('public/surveyValidation', { email: profile.email });

      if (result.isValidUserForSurvey && window.delighted) {
        //  only to be called for selected users
        const surveyPayload = {
          email: profile.email,
          name: profile.name,
          properties: {
            company: profile.selected_schema,
            ...result.isValidUserForSurvey,
          },
        };
        window.delighted.survey(surveyPayload);
      }
    } catch (error) {
      console.log('handleSurvey: error', error);
    }
  };
  useIntercomUserInfo();
  const toggleDrawer = useCallback(
    (open) => {
      const allChartsEle = document.querySelectorAll('[chartId]');
      allChartsEle.forEach((chartDiv) => {
        if (chartDiv) {
          chartDiv.dispatchEvent(new Event('resize'));
        }
      });
      dispatch({
        type: actionTypes.TOGGLE_SIDEBAR,
        sidebarOpen: open,
      });
      setOpen(open);
    },
    [dispatch]
  );

  const getHeader = () => {
    if (usabilityList && usabilityList.length) {
      if (usabilityList[0].lh_category == 'MOBILE') {
        return [
          // { label: 'Site', key: `site` },
          { label: 'Rank', key: `rank` },
          { label: 'Id', key: `row_id` },
          { label: 'Usability Improvement', key: `usability_improvement` },
          { label: 'Category', key: `lh_category` },
          { label: 'Page URL', key: `finalurl_list` },
          // { label: 'Top Pages', key: `top_pages` },
          // { label: 'Top Cat Subcat', key: `top_cat_subcat` },
          { label: 'Organic Clicks Impacted', key: `monthly_clicks` },
          // { label: 'Final URL List', key: `finalurl_list` },
          // { label: 'Usability Impact', key: `usability_impact` },
          // { label: 'URLs Impacted', key: `urls_impacted` },
          { label: 'UUID', key: `uuid` },
          { label: 'Recommendation Status', key: `resolution` },
        ];
      }
    }
    return exportHeaders;
  };

  const SubscriberBanner =
    showSubscriberBanner && subscriberMeta && !isMobile ? (
      <div style={{ background: '#a8dadc', textAlign: 'center', height: isMobile ? 'auto' : '30px', color: 'black' }}>
        <p style={{ lineHeight: isMobile ? 'inherit' : '30px', margin: isMobile ? '0px 39px' : 0 }}>
          <b>
            {subscriberMeta.isInTrial
              ? `Your free trial period ends in ${subscriberMeta.trialDaysRemaining} days.`
              : `Your free trial is about to expire. Ensure uninterrupted service by taking action now.`}
          </b>
          {` `}
          {/* {!subscriberMeta.hasCardAdded
            ? 'Add a payment method to continue without interruption.'
            : 'Explore our plans & pricing to suit your needs.'} */}
          <ManageSubscriptions
            buttonType={'link'}
            isAnchor
            anchorText={!subscriberMeta.hasCardAdded ? 'Add a payment method' : 'Explore our plans & pricing'}
            redirect_url={
              process.env.NEXT_PUBLIC_HOST_URL !== 'http://localhost:3000'
                ? `${process.env.NEXT_PUBLIC_HOST_URL}/app/content-ai-home`
                : 'http://app-predev.quattr.com/app/content-ai-home'
            }
            customer_id={showSubscriberBanner}
          />{' '}
          {!subscriberMeta.hasCardAdded ? 'to continue without interruptions.' : 'to suit your needs.'}
        </p>
        <button
          onClick={() => {
            dispatch({
              type: actionTypes?.SET_SHOW_SUBSCRIPTION_BANNER,
              showSubscriberBanner: false,
            });
          }}
          style={{
            position: 'absolute',
            right: '10px',
            top: '15px',
            transform: 'translateY(-50%)',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
          }}
        >
          <b> &#10005;</b> {/* This is a Unicode character for a cross symbol */}
        </button>
      </div>
    ) : undefined;

  //for checking the magic tool icon visibility
  const isMagicIconVisible = () => {
    const currentTab = selectedUserType || getUserType();
    if (isMobile) {
      //hide for mobile screen
      return false;
    } else if (
      [SUBSCRIPTION_STATUS.SUBSCRIPTION_CANCELLED, SUBSCRIPTION_STATUS.SUBSCRIPTION_PAUSED].includes(
        session?.session?.user?.subscription_status
      )
    ) {
      //hide if subscription cancelled or paused
      return false;
    } else if (session?.session?.user?.userType === USER_CLASSIFICATION?.QWA_TOOLS) {
      //hide if tools user.
      return false;
    } else if (
      session?.session?.user?.userType === USER_CLASSIFICATION?.CONTENT_AI &&
      currentTab === USER_CLASSIFICATION.QWA_TOOLS &&
      [SUBSCRIPTION_STATUS.SUBSCRIPTION_CREATED, SUBSCRIPTION_STATUS.SUBSCRIPTION_TRIAL_END_REMINDER].includes(
        session?.session?.user?.subscription_status
      )
    ) {
      // hide if user is content ai trial user and view's tools tab.
      return false;
    } else {
      //visible for all other users
      return true;
    }
  };

  // handling the user type toggle tracing the locked options and redirecting to particular paths.
  const _handleUserToggle = (value) => {
    const selectedOption = options.find((option) => option.value === value);
    const isLockedValue = selectedOption.isLocked;
    setSelectedUserType(value);
    if (isLockedValue && value === USER_CLASSIFICATION.AI_SEO_SUITE) {
      router.push('/app/ai-seo-suite-home');
    } else if (isLockedValue && value === USER_CLASSIFICATION.CONTENT_AI) {
      router.push('/app/content-ai-home');
    } else {
      setSelectedUserType(value);
      if (value === USER_CLASSIFICATION.CONTENT_AI && !pathname.includes('/app/content-ai-home')) {
        router.push('/app/content-ai-home');
      } else if (
        value === USER_CLASSIFICATION.QWA_TOOLS &&
        !pathname.includes('/app/ai-tools-home', '/app/content-ai-home')
      ) {
        router.push('/app/ai-tools-home');
      } else if (value === USER_CLASSIFICATION.AI_SEO_SUITE && !pathname.includes('/app/trends/growth-trends')) {
        router.push('/app/trends/growth-trends');
      }
    }
  };

  //set initial user type
  useEffect(() => {
    const userType = getUserType();
    if (userType === USER_CLASSIFICATION.CONTENT_AI) {
      setOptions([
        { text: 'AI SEO Suite', value: 'AI_SEO_SUITE', disabled: false, isLocked: true, hidden: false },
        { text: 'Content AI', value: 'CONTENT_AI', disabled: false, isLocked: false, hidden: false },
        { text: 'AI Tools', value: 'QWA_TOOLS', disabled: false, isLocked: false, hidden: false },
      ]);
    } else if (userType === USER_CLASSIFICATION.QWA_TOOLS) {
      setOptions([
        { text: 'AI SEO Suite', value: 'AI_SEO_SUITE', disabled: true, isLocked: true, hidden: false },
        { text: 'Content AI', value: 'CONTENT_AI', disabled: false, isLocked: true, hidden: false },
        { text: 'AI Tools', value: 'QWA_TOOLS', disabled: false, isLocked: false, hidden: false },
      ]);
    } else if (getUserType() == undefined) {
      setOptions([
        { text: 'AI SEO Suite', value: 'AI_SEO_SUITE', disabled: false, isLocked: false, hidden: false },
        { text: 'Content AI', value: 'CONTENT_AI', disabled: false, isLocked: false, hidden: true },
        { text: 'AI Tools', value: 'QWA_TOOLS', disabled: false, isLocked: false, hidden: false },
      ]);
    } else {
      setOptions([
        { text: 'AI SEO Suite', value: 'AI_SEO_SUITE', disabled: false, isLocked: false, hidden: false },
        { text: 'Content AI', value: 'CONTENT_AI', disabled: false, isLocked: false, hidden: false },
        { text: 'AI Tools', value: 'QWA_TOOLS', disabled: false, isLocked: false, hidden: false },
      ]);
    }
  }, [session, customerInfo]);

  //handle home screen icons clicks and path change.

  useEffect(() => {
    if (
      router.pathname.startsWith('/app/ai-tools/') ||
      ['/app/ai-tools-home', '/app/add-credits', '/app/manage-account'].includes(router.pathname)
    ) {
      setSelectedUserType(USER_CLASSIFICATION.QWA_TOOLS);
    } else if (
      ['/app/content-ai-home'].includes(router.pathname) ||
      router.pathname.startsWith('/app/content-ai-home')
    ) {
      setSelectedUserType(USER_CLASSIFICATION.CONTENT_AI);
    } else if (
      getUserType() === USER_CLASSIFICATION.CONTENT_AI &&
      (['/app/manage-lists/keywords-list', '/app/content-ai-home'].includes(router.pathname) ||
        router.pathname.startsWith('/app/content-recommendations'))
    ) {
      setSelectedUserType(USER_CLASSIFICATION.CONTENT_AI);
    } else if (router.pathname === '/app/ai-seo-suite-home') {
      setSelectedUserType(USER_CLASSIFICATION.AI_SEO_SUITE);
    }
  }, [router.pathname]);

  const handleCustomPopup = (value) => {
    dispatch({
      type: actionTypes?.SET_CUSTOM_POPUP_STATUS,
      customPopupConfig: { ...customPopupConfig, showPopup: value },
    });
  };

  const isDisplayUseDesktopAlert = useMemo(() => {
    const path1 = router?.pathname?.includes('ai-tools');
    const path2 = router?.pathname?.includes('ai-tools-home');
    return isMobile && isFreeTrailContentAIUser && !(path1 || path2);
  }, [isMobile, router?.pathname, isFreeTrailContentAIUser]);

  return (
    <>
      {session.status === 'loading' || loadersArray.includes('PAGE_LOADER') ? (
        <ThreeBarLoader />
      ) : (
        <div className="wrapper">
          <Header
            isMobile={isMobile}
            isEnterpriseUser={getUserType() == undefined}
            defaultUser={selectedUserType || getUserType()}
            options={options}
            open={open}
            toggleDrawer={toggleDrawer}
            handleUserToggle={_handleUserToggle}
            SubscriberBanner={SubscriberBanner}
            userType={selectedUserType || getUserType()}
            getUserType={getUserType()}
          />
          <Sidebar
            isMobile={isMobile}
            open={open}
            options={options}
            userType={selectedUserType || getUserType()}
            defaultUser={getUserType()}
            selectedUserType={selectedUserType}
            isEnterpriseUser={getUserType() == undefined}
            handleUserToggle={_handleUserToggle}
            toggleDrawer={toggleDrawer}
          />
          <div
            className={`main ${open ? 'sidebar-open' : 'sidebar-close'} `}
            style={
              showSubscriberBanner && !isMobile
                ? isMobile
                  ? { height: 'calc(100vh - 120px)', marginTop: '128px' }
                  : { height: 'calc(100vh - 94px)', marginTop: '94px' }
                : { height: 'calc(100vh - 64px)', marginTop: '68px' }
            }
          >
            <IconButton onClick={() => toggleDrawer(!open)} className="sidebarToggleBtn">
              {!open ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
            <div
              className={`${showFullScreen ? '' : 'main-wrapper'} ${
                isDisplayUseDesktopAlert ? 'main-wrapper-desktop-alert' : ''
              }  `}
            >
              {!showFullScreen &&
                !shoudHideQheader &&
                (isSiteUsabilityPage ? (
                  <QsectionHeader exportData={{ header: getHeader(), data: usabilityList }} />
                ) : (
                  <QsectionHeader />
                ))}
              {loadersArray.includes('SECTIONAL-LOADER') ? (
                <div id="layout__div__private_layout_3" className="position-rel h-vertical-center">
                  <ThreeBarLoader />
                </div>
              ) : (
                <div className="ui-body-container">
                  <div>{props.children}</div>
                </div>
              )}
              {snackConfig.open ? (
                <Qalert
                  type={snackConfig.type}
                  open={snackConfig.open}
                  set={(flag) => toogleSnackBar({ open: flag })}
                  text={snackConfig.text}
                  time={snackConfig?.time || 5000}
                />
              ) : null}
              {customPopupConfig?.showPopup ? (
                <CustomAlert
                  showPopup={customPopupConfig?.showPopup}
                  style={{ fontSize: '12px' }}
                  setAlertControl={handleCustomPopup}
                  text={customPopupConfig?.text}
                  anchorText={customPopupConfig?.anchorText}
                  anchorLink={customPopupConfig?.anchorLink}
                />
              ) : null}
              {isDisplayUseDesktopAlert ? <DesktopAlert /> : null}
            </div>
          </div>
          {isMagicIconVisible() ? <AutoWriteEditor /> : null}
        </div>
      )}
    </>
  );
};
export default PrivateLayout;
